import {GeneralUtility} from '../utilities/general-utility';
import {Settings} from '../utilities/settings';
import mitt, {Emitter} from 'mitt';
import {AbstractDataApiPlugin, PluginFilter, PluginSettings} from './abstract-data-api-plugin';

type FilterEvent<T extends PluginFilter> = {
  change: T
};

/**
 * Base for a filter plugin.
 *
 * @author Philipp Seiler <sep@earlybird.at>
 */
export abstract class AbstractFilter<
  T extends PluginFilter,
  S extends PluginSettings
> extends AbstractDataApiPlugin<S> {
  public emitter: Emitter<FilterEvent<T>> = mitt<FilterEvent<T>>();
  protected filterIdentifier: string;
  private readonly _defaultFilter: any;
  private _filter: any;
  protected hashParameters: { [key: string]: string } | null = null;
  protected textFields: NodeListOf<HTMLInputElement> | null = null;
  protected selectsFields: NodeListOf<HTMLSelectElement> | null = null;
  protected valueButtons: NodeListOf<HTMLButtonElement> | null = null;
  protected resetButton: HTMLButtonElement | null = null;
  protected searchButton: HTMLButtonElement | null = null;

  /**
   * Checks if none of the filter values are set.
   *
   * @param {T|null} filter Filter to check
   * @returns {boolean} True when all filter values are empty, otherwise false
   */
  public static isFilterEmpty<T extends any>(filter: T | null): boolean {
    if (filter === null) {
      return true;
    }
    for (const key in filter) {
      if (filter[key]) {
        return false;
      }
    }
    return true;
  }

  constructor(container: HTMLElement, filterIdentifier: string, defaultFilter: T) {
    super(container);
    this.filterIdentifier = filterIdentifier;
    this._defaultFilter = JSON.parse(JSON.stringify(defaultFilter));

    // Initialize default filter settings
    const settings = Settings.instance.settings;
    if (settings.filter === null) {
      settings.filter = {};
      Settings.instance.set(settings);
    }
    if (!(this.filterIdentifier in settings.filter)) {
      settings.filter[this.filterIdentifier] = this.cloneDefaultFilter();
      Settings.instance.set(settings);
    }
    if (window.location.hash) {
      const parts = window.location.hash.replace('#', '').split(',');
      for (const part of parts) {
        const filter = part.split('=');
        if (filter.length === 2) {
          if (this.hashParameters === null) {
            this.hashParameters = {};
          }
          this.hashParameters[filter[0]] = filter[1];
        }
      }
    }
    this._filter = this.loadFilter();
  }

  /**
   * Gets the current filter.
   *
   * @returns {T}
   */
  public get filter(): T {
    return this._filter;
  }

  /**
   * Gets the current filter.
   *
   * @param {T} filter Filter
   */
  public set filter(filter: T) {
    this._filter = filter;
  }

  /**
   * Gets the default filter.
   *
   * @returns {T}
   */
  public get defaultFilter(): T {
    return this._defaultFilter;
  }

  /**
   * Creates a clone of the default filter.
   *
   * @returns {T}
   */
  protected cloneDefaultFilter(): T {
    return JSON.parse(JSON.stringify(this.defaultFilter));
  }

  /**
   * Loads a saved filter from within the current identifier namespace.
   *
   * @returns {T} Filter, if found
   */
  protected loadFilter(): T {
    const settings = Settings.instance.settings;
    if (!(this.filterIdentifier in settings.filter)) {
      throw new Error(`Unknown filter identifier: ${this.filterIdentifier}.`);
    }
    let filter = settings.filter[this.filterIdentifier];
    if (filter === null) {
      filter = this.cloneDefaultFilter();
    }

    // Check if a URL parameter overrides a filter
    for (const key in filter) {
      const urlParameter = this.getFilterFromHash(key);
      if (urlParameter !== null && key in filter) {
        filter[key] = urlParameter;
      }
    }
    this.filter = filter;
    return filter;
  }

  /**
   * Saves a filter within the current identifier namespace.
   *
   * @param {T} filter Filter to save
   * @returns {T}
   */
  private saveFilter(filter: T): T {
    const settings = Settings.instance.settings;
    if (!(this.filterIdentifier in settings.filter)) {
      throw new Error(`Unknown filter identifier: ${this.filterIdentifier}.`);
    }
    settings.filter[this.filterIdentifier] = filter;
    Settings.instance.set(settings);
    this.filter = filter;
    return filter;
  }

  /**
   * Saves a single filter value within the current identifier namespace.
   *
   * @param {string} name Name of the filter to save
   * @param {string} value Value to save
   * @returns {T}
   */
  private saveFilterValue(name: string, value: string): T {
    const filter = this.loadFilter() as any;
    if (!(name in filter)) {
      throw new Error(`Unknown filter: ${name}.`);
    }
    const valueAsInt = parseInt(value);
    if (!isNaN(valueAsInt)) {
      filter[name] = valueAsInt;
    } else {
      filter[name] = value;
    }
    this.saveFilter(filter);
    this.filter = filter;
    return filter;
  }

  /**
   * Tries to get filter values from the URL hash.
   *
   * @param {string} name Name of the filter to get
   * @returns {string|null} Filter value, if found
   */
  private getFilterFromHash(name: string): string | null {
    if (this.hashParameters === null || !(name in this.hashParameters)) {
      return null;
    }
    return this.hashParameters[name].trim();
  }

  /**
   * Initializes the result container.
   */
  protected initializeResult(): void {
    if (!this.resultContainer) {
      return;
    }
    this.textFields = this.resultContainer.querySelectorAll<HTMLInputElement>('input[type="text"]');
    this.selectsFields = this.resultContainer.querySelectorAll<HTMLSelectElement>('select');
    this.valueButtons = this.resultContainer.querySelectorAll<HTMLButtonElement>('[data-value][data-name]');
    this.resetButton = this.resultContainer.querySelector<HTMLButtonElement>('button[type="reset"]');
    this.searchButton = this.resultContainer.querySelector<HTMLButtonElement>('button[type="submit"]');
    this.initializeTextFields();
    this.initializeSelectFields();
    this.initializeValueButtons();
    this.initializeResetButton();
    this.initializeSearchButton();
  }

  /**
   * Resets all filters.
   */
  private reset(): void {
    if (this.textFields !== null && this.textFields.length > 0) {
      for (const field of this.textFields) {
        if (field.name in this._defaultFilter) {
          field.value = this._defaultFilter[field.name];
        }
      }
    }
    if (this.selectsFields && this.selectsFields.length > 0) {
      for (const field of this.selectsFields) {
        if (field.name in this._defaultFilter) {
          field.value = this._defaultFilter[field.name];
        }
      }
    }
    if (this.valueButtons && this.valueButtons.length > 0) {
      for (const button of this.valueButtons) {
        const name = button.dataset.name ?? '';
        if (name in this._defaultFilter) {
          if (button.dataset.defaultValue) {
            button.dataset.active = 'true';
          } else {
            button.dataset.active = 'false';
          }
        }
      }
    }
    this.saveFilter(this.cloneDefaultFilter());
  }

  /**
   * Initializes text field filters.
   */
  private initializeTextFields(): void {
    if (this.textFields === null || this.textFields.length === 0) {
      return;
    }
    for (const field of this.textFields) {
      // Restore saved filter
      if (field.name in this._filter) {
        field.value = this._filter[field.name];
      }

      GeneralUtility.debouncedSearch(field, () => {
        this.saveFilterValue(field.name, field.value);
        this.emitter.emit('change', this.filter);
      });
    }
  }

  /**
   * Initializes select field filters.
   */
  private initializeSelectFields(): void {
    if (this.selectsFields === null || this.selectsFields.length === 0) {
      return;
    }
    for (const field of this.selectsFields) {
      // Restore saved filter
      if (field.name in this._filter) {
        field.value = this._filter[field.name];
      }

      field.addEventListener('change', () => {
        this.saveFilterValue(field.name, field.value);
        this.emitter.emit('change', this.filter);
      });
    }
  }

  /**
   * Initializes value button filters.
   */
  private initializeValueButtons(): void {
    if (this.valueButtons === null || this.valueButtons.length === 0) {
      return;
    }
    for (const button of this.valueButtons) {
      const name = button.dataset.name ?? '';
      const value = (button.dataset.value ?? '').toString();

      // Restore saved filter
      if (name in this._filter) {
        if (value === this._filter[name].toString()) {
          button.dataset.active = 'true';
        } else {
          button.dataset.active = 'false';
        }
      }

      button.addEventListener('click', () => {
        // Reset all buttons belonging to this name/group
        if (this.valueButtons !== null) {
          for (const otherButton of this.valueButtons) {
            if (otherButton.dataset.name === name) {
              otherButton.dataset.active = 'false';
            }
          }
        }
        button.dataset.active = 'true';

        this.saveFilterValue(name, value);
        this.emitter.emit('change', this.filter);
      });
    }
  }

  /**
   * Initializes the button to reset the filters.
   */
  private initializeResetButton(): void {
    if (this.resetButton === null) {
      return;
    }
    this.resetButton.addEventListener('click', e => {
      e.preventDefault();
      this.reset();
      this.emitter.emit('change', this.filter);
    });
  }

  /**
   * Initializes the button to perform the search.
   */
  private initializeSearchButton(): void {
    if (this.searchButton === null) {
      return;
    }
    this.searchButton.addEventListener('click', e => {
      e.preventDefault();
      this.emitter.emit('change', this.filter);
    });
  }
}
